import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Seo } from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { RingsField } from "@/components/Rings";
import { BRANDS } from "@/data/brands";

export default function Brands() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [-60, 60]);

  return (
    <div data-testid="brands-page" ref={ref} className="relative">
      <Seo
        title="The brands — Antelli"
        description="The five Antelli brands: PB Travel Angel, Cloud My Luxury, The Expat Studio, StayCraft Studio and Soft Spine Press — what each does and who it is for."
      />
      <motion.div
        style={{ y }}
        className="pointer-events-none absolute -left-48 top-40 w-[520px] opacity-60"
        aria-hidden="true"
      >
        <RingsField />
      </motion.div>

      <div className="max-w-4xl mx-auto px-6 sm:px-10 pt-24 pb-24 sm:pt-36 sm:pb-36 relative">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.3em] text-[#7A6034]">The brands</p>
          <h1
            data-testid="brands-heading"
            className="mt-6 font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-tight text-[#1A1C23] font-medium max-w-2xl"
          >
            Five brands, one standard of care
          </h1>
          <p className="mt-8 text-base sm:text-lg leading-relaxed text-[#4A4D57] max-w-xl">
            Each brand works in its own field, speaks in its own voice and
            keeps its own house. What they share is a way of doing things:
            honest, detailed and made to last. Every one of them lives on its
            own site — the links below take you there.
          </p>
        </Reveal>

        <div className="mt-20 sm:mt-28 space-y-0">
          {BRANDS.map((brand, i) => (
            <Reveal key={brand.id} delay={0.05}>
              <article
                data-testid={`brands-entry-${brand.id}`}
                className="border-t border-[#1A1C23]/10 py-14 sm:py-20 grid grid-cols-1 md:grid-cols-12 gap-8"
              >
                <div className="md:col-span-3">
                  <span
                    className="font-serif-display text-3xl sm:text-4xl"
                    style={{ color: brand.accent }}
                  >
                    {brand.number}
                  </span>
                  <span
                    className="mt-4 block h-px w-12"
                    style={{ backgroundColor: brand.accent }}
                  />
                </div>
                <div className="md:col-span-9">
                  <h2 className="font-serif-display text-3xl sm:text-4xl text-[#1A1C23]">
                    {brand.name}
                  </h2>
                  <p className="mt-6 text-base leading-relaxed text-[#4A4D57] max-w-2xl">
                    {brand.long}
                  </p>
                  <a
                    href={brand.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`brands-visit-${brand.id}`}
                    className="group mt-8 inline-flex items-center gap-2 text-sm text-[#7A6034] hover:text-[#1A1C23] transition-colors duration-300"
                  >
                    <span className="border-b border-[#B69766]/50 group-hover:border-[#1A1C23]/50 pb-0.5 transition-colors duration-300">
                      Visit {brand.domain}
                    </span>
                    <ArrowUpRight
                      size={15}
                      strokeWidth={1.5}
                      className="transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
                    />
                  </a>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </div>
    </div>
  );
}
